"use client";

import type { Monaco } from "@monaco-editor/react";
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ListTree, ScanSearch, XIcon } from "lucide-react";

import type { Node } from "@acme/ast-grep-wasm-loader";
import { cn } from "@acme/ui";
import { Button } from "@acme/ui/button";
import { Checkbox } from "@acme/ui/checkbox";
import { Label } from "@acme/ui/label";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@acme/ui/resizable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@acme/ui/tabs";
import { toast } from "@acme/ui/toast";
import { StudioMode } from "@acme/validators";

import { AskCodemodBanner } from "~/app/_components/ask-codemod-banner";
import { umami } from "~/utils/analytics";
import { useSession } from "~/utils/auth";
import {
  buildRules,
  useAstGrep,
  useDumpASTNodes,
  useInitializeSharedSession,
} from "../_hooks/ast-grep-hooks";
import { useCodemodAiPanel } from "../_hooks/use-codemod-ai-panel";
import {
  useActiveEditorInstance,
  useInitializeMonacoEditor,
  useMonacoEditor,
} from "../_hooks/use-monaco-editor";
import { useSelectedAstNode } from "../_hooks/use-selected-ast-node";
import { useStudioStore } from "../_hooks/use-studio-store";
import CodemodStudioLoading from "../loading";
import { CopyJsonIcon } from "./_icons/copy-json";
import { CopyYamlIcon } from "./_icons/copy-yaml";
import {
  getMonacoLanguage,
  injectJsAstGrepTypes,
  monacoEditorOptionsWithSyntaxValidation,
  useMonacoEditorTheme,
} from "./_utils/monaco-options";
import { CodemodAI } from "./codemod-ai";
import { DebugPanel } from "./debug-panel";
import { MatchList } from "./match-list";
import { LeftPanel } from "./panels/left-panel";
import { ShareModal } from "./project-dropdown/share-modal";
import { RuleErrors } from "./rule-errors";
import { SourcesTabs } from "./sources-tabs";
import { TreeNode } from "./tree-node";
import { YamlAstGrepEditor } from "./yaml-ast-grep-editor";

interface StudioPageContentProps {
  showRemoteRun?: boolean;
  className?: string;
}

export default function CodemodStudioPage({
  showRemoteRun = true,
  className,
}: StudioPageContentProps) {
  const editorTheme = useMonacoEditorTheme();

  const [showUnnamedNodes, setShowUnnamedNodes] = useState(false);
  const [showPosition, setShowPosition] = useState(true);
  const [showCodemodBanner, setShowCodemodBanner] = useState(false);
  const [showFooter, setShowFooter] = useState(true);
  const [aiModalThreadId, setAiThreadId] = useCodemodAiPanel();
  const { status: authStatus } = useSession();

  const aiModalOpen = !!aiModalThreadId;

  useEffect(() => {
    void umami.track(
      aiModalOpen ? "studio:ai-modal:open" : "studio:ai-modal:close",
    );
  }, [aiModalOpen]);

  const getCurrentState = useStudioStore((state) => state.getCurrentState);
  const mode = useStudioStore((state) => state.mode);

  const setJsAstGrepCodemod = useStudioStore(
    (state) => state.setJsAstGrepCodemod,
  );
  const jsAstGrepCodemod = useStudioStore((state) => state.jsAstGrepCodemod);
  const lang = useStudioStore((state) => state.lang);
  const { matchedHighlights, rewrittenCode, logs, handleClearLogs } =
    useAstGrep();

  useInitializeMonacoEditor();

  const { monacoEditorModule, monacoReactModule } = useMonacoEditor();
  const rootNode = useDumpASTNodes();

  const activeEditorInstance = useActiveEditorInstance();

  const {
    selectedNodeInEditor,
    clearSelectedNode,
    setHoveringNodeInTree,
    setHoveringNodeInMatchList,
    hoveringNodeInTree,
    hoveringNodeInMatchList,
  } = useSelectedAstNode(activeEditorInstance, matchedHighlights, rootNode);

  const { isLoadingSharedSession } = useInitializeSharedSession();

  const handleJsAstGrepEditorPreMount = useCallback((monaco: Monaco) => {
    injectJsAstGrepTypes(monaco);

    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    });
  }, []);

  const handleHoverNodeInTree = useCallback(
    (node: Node | null) => {
      setHoveringNodeInTree(node);
    },
    [setHoveringNodeInTree],
  );

  const copyAsJson = useCallback(() => {
    const json = JSON.stringify(buildRules(getCurrentState()), null, 2);
    void navigator.clipboard.writeText(json);
    void umami.track("studio:copy-as-json");
    toast.success("Copied to clipboard");
  }, [getCurrentState]);

  const copyAsYaml = useCallback(() => {
    const yaml = getCurrentState().config;
    void navigator.clipboard.writeText(yaml);
    void umami.track("studio:copy-as-yaml");
    toast.success("Copied to clipboard");
  }, [getCurrentState]);

  const handleCloseAiModal = useCallback(() => {
    void setAiThreadId(null);
  }, [setAiThreadId]);

  const handleCloseFooter = useCallback(() => {
    setShowFooter(false);
  }, []);

  if (!monacoEditorModule || !monacoReactModule || isLoadingSharedSession) {
    return <CodemodStudioLoading />;
  }

  const Editor = monacoReactModule.Editor;

  const [colSize1, colSize2, colSize3] = aiModalOpen
    ? [27, 27, 26]
    : [45, 35, 0];

  return (
    <>
      {/* Header is 36px. Footer is 30px. So 36 + 30 = 66 */}
      <motion.div
        className={cn(className)}
        animate={{
          height: showFooter ? "calc(100vh - 66px)" : "calc(100vh - 36px)",
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1],
        }}
      >
        <ResizablePanelGroup
          direction="horizontal"
          className="size-full"
          autoSaveId="conditional"
        >
          <LeftPanel showRemoteRun={showRemoteRun} authStatus={authStatus} />
          <ResizablePanel
            defaultSize={colSize1}
            minSize={25}
            id="source-panel"
            order={2}
          >
            <ResizablePanelGroup direction="vertical">
              <ResizablePanel
                id="source-editor-panel"
                defaultSize={70}
                className="flex flex-col"
              >
                <SourcesTabs
                  rewrittenCode={rewrittenCode}
                  editorTheme={editorTheme}
                />
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel
                id="ast-viewer-panel"
                defaultSize={30}
                className="border-t-2"
              >
                <div className="border-b-border bg-background flex w-full flex-row items-center gap-1 border-b px-2 py-2 shadow dark:shadow-xl">
                  <h3 className="flex items-center gap-1 truncate font-medium">
                    <ListTree className="size-5" />
                    AST Tree
                  </h3>
                  <div className="flex-1" />
                  <Label className="flex items-center gap-1 truncate text-xs">
                    <Checkbox
                      checked={showUnnamedNodes}
                      onCheckedChange={(checked) =>
                        setShowUnnamedNodes(!!checked)
                      }
                    />
                    Show full tree
                  </Label>
                  <Label className="flex items-center gap-1 truncate text-xs">
                    <Checkbox
                      checked={showPosition}
                      onCheckedChange={(checked) => setShowPosition(!!checked)}
                    />
                    Show position
                  </Label>
                </div>
                <TreeNode
                  showUnnamedNodes={showUnnamedNodes}
                  showPosition={showPosition}
                  onHoverNode={handleHoverNodeInTree}
                  hoveringNodeInTree={hoveringNodeInTree}
                  selectedNodeInEditor={selectedNodeInEditor}
                  hoveringNodeInMatchList={hoveringNodeInMatchList}
                  rootNode={rootNode}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel
            defaultSize={colSize2}
            minSize={25}
            className="border-l-2"
            id="right-panel"
            order={3}
          >
            <ResizablePanelGroup direction="vertical">
              <ResizablePanel id="rule-editor-panel" defaultSize={70}>
                <Tabs value={mode} className="h-full">
                  <TabsList
                    className="border-b-border flex h-[40px] justify-start gap-1 border-b pt-1"
                    variant="underline"
                  >
                    {mode === StudioMode.Config && (
                      <TabsTrigger
                        variant="underline"
                        className="flex items-center gap-1 font-mono text-sm"
                        value={StudioMode.Config}
                      >
                        YAML
                      </TabsTrigger>
                    )}
                    {mode === StudioMode.JsAstGrep && (
                      <TabsTrigger
                        variant="underline"
                        className="flex items-center gap-1 font-mono text-sm"
                        value={StudioMode.JsAstGrep}
                      >
                        Snippet
                      </TabsTrigger>
                    )}
                  </TabsList>
                  <TabsContent
                    value={StudioMode.Config}
                    className="h-[calc(100%-2.5rem)] border-none p-0 pl-0.5 data-[state=active]:block"
                  >
                    <div className="flex h-[calc(100%-2.5rem)] flex-col">
                      <AskCodemodBanner
                        showCodemodBanner={showCodemodBanner}
                        setShowCodemodBanner={setShowCodemodBanner}
                      />

                      <div
                        className={cn(
                          showCodemodBanner ? "h-[calc(100%-44px)]" : "h-full",
                        )}
                      >
                        <YamlAstGrepEditor
                          editorTheme={editorTheme}
                          monacoEditorOptions={
                            monacoEditorOptionsWithSyntaxValidation
                          }
                        />
                      </div>
                    </div>

                    <div className="flex h-10 items-center border-t bg-gray-100 dark:bg-black">
                      <RuleErrors />
                      <div className="flex-1" />
                      {mode === StudioMode.Config && (
                        <>
                          <Button
                            variant="ghost"
                            className="gap-1 rounded-none text-xs"
                            onClick={copyAsJson}
                          >
                            <CopyJsonIcon className="size-4" />
                            Copy as JSON
                          </Button>
                          <Button
                            variant="ghost"
                            className="gap-1 rounded-none text-xs"
                            onClick={copyAsYaml}
                          >
                            <CopyYamlIcon className="size-4" />
                            Copy as YAML
                          </Button>
                        </>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent
                    value={StudioMode.JsAstGrep}
                    className="h-[calc(100%-2.5rem)] border-none p-0 data-[state=active]:block"
                  >
                    <Editor
                      className="flex-1"
                      language="typescript"
                      value={jsAstGrepCodemod}
                      onChange={(value) => setJsAstGrepCodemod(value ?? "")}
                      theme={editorTheme}
                      options={monacoEditorOptionsWithSyntaxValidation}
                      beforeMount={handleJsAstGrepEditorPreMount}
                    />
                  </TabsContent>
                </Tabs>
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel
                id="matches-or-console"
                defaultSize={30}
                className="border-t-2"
              >
                {mode === StudioMode.Config ? (
                  <>
                    <div className="border-b-border bg-background flex w-full flex-row items-center justify-between gap-4 border-b px-2 py-2 shadow dark:shadow-xl">
                      <h3 className="flex items-center gap-1 font-medium">
                        <ScanSearch className="size-5" />
                        Matches
                      </h3>

                      <div className="text-muted-foreground font-mono text-sm">
                        {matchedHighlights.length > 0 ? (
                          <span>
                            Found <span>{matchedHighlights.length}</span>{" "}
                            match(es)
                          </span>
                        ) : (
                          <span>No matches found</span>
                        )}
                      </div>
                    </div>
                    <MatchList
                      matchedHighlights={matchedHighlights}
                      setHoveredMatch={setHoveringNodeInMatchList}
                      hoveredMatch={hoveringNodeInMatchList}
                      clearSelectedNode={clearSelectedNode}
                      lang={getMonacoLanguage(lang)}
                    />
                  </>
                ) : (
                  <DebugPanel logs={logs} onClearLogs={handleClearLogs} />
                )}
              </ResizablePanel>
            </ResizablePanelGroup>
          </ResizablePanel>
          {aiModalOpen && (
            <>
              <ResizableHandle />
              <ResizablePanel
                id="codemod-ai"
                defaultSize={colSize3}
                minSize={20}
                order={4}
              >
                <CodemodAI onClose={handleCloseAiModal} />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </motion.div>
      <AnimatePresence>
        {showFooter && (
          <motion.footer
            className="bg-background text-muted-foreground fixed right-0 bottom-px left-16 z-10000 ml-px flex h-[30px] items-center gap-0.5 border-t-2 p-0.5 text-xs"
            initial={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.3,
                ease: [0.4, 0.0, 0.2, 1],
              },
            }}
          >
            <span className="inline-flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className="size-6 rounded-full"
                onClick={handleCloseFooter}
              >
                <XIcon />
              </Button>
              <span>Check out the</span>
              <Button
                size="sm"
                className="px-0.5 py-0 text-xs"
                variant="link"
                asChild
              >
                <a
                  data-umami-event="studio:ast-grep-docs"
                  href="https://ast-grep.github.io/guide/introduction.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  ast-grep documentation
                </a>
              </Button>
              <span>for details.</span>
            </span>

            <span>Prefer jscodeshift? Check out our</span>
            <Button
              size="sm"
              className="px-0.5 py-0 text-xs"
              variant="link"
              asChild
            >
              <a
                href="https://codemod.com/studio-jscodeshift"
                target="_blank"
                rel="noreferrer"
              >
                legacy studio
              </a>
            </Button>
          </motion.footer>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <ShareModal />
    </>
  );
}
